import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import { getRetailerInfo } from '../../../actions/retailerUsersActions';

const MessagingRuleReassignModal = (props) => {
  const { rule, pendingActivation, onSuccess } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [reassignForm, setReassignForm] = useState({
    waitTime: rule?.additional_data?.wait_time || '',
    maxReassignments: rule?.additional_data?.max_reassignments || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (rule?.additional_data) {
      setReassignForm({
        waitTime: rule.additional_data.wait_time || '',
        maxReassignments: rule.additional_data.max_reassignments || ''
      });
    }
  }, [rule]);

  const showToast = (message) => {
    if (window.showtoast) {
      window.showtoast(message);
    }
  };

  const close = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'MessagingRuleReassignModal' }
    });
  };

  const handleSave = async () => {
    if (!rule?.id) {
      setError('Error: Rule context missing');
      return;
    }

    if (!reassignForm.waitTime || !reassignForm.maxReassignments) {
      setError(t('messaging_rules.errors.reassign_selection'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        wait_time: reassignForm.waitTime,
        max_reassignments: reassignForm.maxReassignments
      };

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch('/api/v1/business_rules/save_additional_data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          rule_id: rule.id,
          data: payload
        })
      });

      if (!response.ok) throw new Error('Error saving data');
      const data = await response.json();

      if (pendingActivation) {
        await fetch('/api/v1/business_rules/manage_retailer_rule', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ rule_id: rule.id })
        });
      }

      showToast(data?.message || t('messaging_rules.success.data_saved'));
      dispatch(getRetailerInfo({ force: true }));
      if (onSuccess) onSuccess();
      close();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError(t('CommonMC:errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <RightModalLayout
      title={t('messaging_rules.configure')}
      submitAction={handleSave}
      submitLabel={t('CommonMC:actions.save', { defaultValue: 'Guardar' })}
      cancelLabel={t('CommonMC:actions.cancel', { defaultValue: 'Cancelar' })}
      cancelAction={close}
      closeAction={close}
      submitDisabled={saving}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <div>
          <p className="tw-text-sm tw-font-medium">{t('messaging_rules.reassign_wait_time')}</p>
          <p className="tw-text-xs tw-text-gray-500 tw-mb-2">{t('messaging_rules.reassign_wait_time_desc')}</p>
          <select
            className="tw-border tw-border-gray-200 tw-rounded-xl tw-w-full tw-py-2 tw-px-3"
            value={reassignForm.waitTime}
            onChange={(e) => setReassignForm((prev) => ({ ...prev, waitTime: e.target.value }))}
          >
            <option value="">{t('messaging_rules.select')}</option>
            {Object.entries(t('messaging_rules.wait_time_options', { returnObjects: true }) || {}).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <p className="tw-text-sm tw-font-medium">{t('messaging_rules.reassign_max')}</p>
          <p className="tw-text-xs tw-text-gray-500 tw-mb-2">{t('messaging_rules.reassign_max_desc')}</p>
          <select
            className="tw-border tw-border-gray-200 tw-rounded-xl tw-w-full tw-py-2 tw-px-3"
            value={reassignForm.maxReassignments}
            onChange={(e) => setReassignForm((prev) => ({ ...prev, maxReassignments: e.target.value }))}
          >
            <option value="">{t('messaging_rules.select')}</option>
            {Object.entries(t('messaging_rules.max_reassign_options', { returnObjects: true }) || {}).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        {error && <p className="tw-text-xs tw-text-red-500">{error}</p>}
      </div>
    </RightModalLayout>
  );
};

export default MessagingRuleReassignModal;
